import React from 'react';

function TermsOfService() {
  const handleBack = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          {/* Back Button */}
          <div className="mb-6">
            <button
              onClick={handleBack}
              className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span className="mr-2">←</span>
              Back
            </button>
          </div>

          <h1 className="text-4xl font-bold text-white mb-8 text-center">
            Terms of Service
          </h1>
          
          <div className="text-gray-300 space-y-6">
            <p className="text-sm text-gray-400 mb-8">
              <strong>Last Updated:</strong> February 26, 2026
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                By accessing or using actuvo.ai ("Service", "Platform", "Bot"), you agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree to these Terms, do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="mb-4">
                actuvo.ai is a grid trading platform for Hyperliquid that places automated buy and sell orders across a price range to capture market volatility.
                The Service integrates with Hyperliquid exchange to execute trades in real-time, 24/7.
              </p>
              <div className="bg-red-900 border border-red-700 p-4 rounded-lg">
                <p className="text-red-100 font-semibold">
                  ⚠️ IMPORTANT: Trading involves substantial risk of loss. Past performance does not guarantee future results. 
                  You may lose some or all of your invested capital.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. User Responsibilities</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>You must be at least 18 years old and legally capable of entering contracts</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must provide accurate, current, and complete information during registration</li>
                <li>You are responsible for all activities that occur under your account</li>
                <li>You must comply with all applicable laws and regulations regarding trading and investments</li>
                <li>You must configure your own Hyperliquid API credentials and accept responsibility for their security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Financial Risk Disclaimers</h2>
              <div className="bg-yellow-900 border border-yellow-700 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-yellow-100 mb-3">HIGH RISK INVESTMENT WARNING</h3>
                <ul className="list-disc list-inside space-y-2 text-yellow-100">
                  <li><strong>SUBSTANTIAL RISK:</strong> Trading financial instruments carries a high level of risk and may not be suitable for all investors</li>
                  <li><strong>POTENTIAL LOSSES:</strong> You may lose some or all of your initial investment</li>
                  <li><strong>NO GUARANTEES:</strong> Past performance is not indicative of future results</li>
                  <li><strong>VOLATILITY:</strong> Prices of financial instruments can be highly volatile</li>
                  <li><strong>LEVERAGE RISKS:</strong> Leveraged trading can amplify both profits and losses</li>
                  <li><strong>TECHNICAL RISKS:</strong> System failures, connectivity issues, or API limitations may affect trading</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Limitation of Liability</h2>
              <div className="bg-gray-700 p-4 rounded-lg">
                <p className="mb-4">
                  <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>actuvo.ai provides the service "AS IS" without any warranties, express or implied</li>
                  <li>We do not guarantee continuous, uninterrupted access to the service</li>
                  <li>We are not liable for any trading losses, missed opportunities, or technical failures</li>
                  <li>Our total liability shall not exceed the amount you paid for the service in the past 12 months</li>
                  <li>We are not responsible for third-party services (Hyperliquid, market data providers, etc.)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Service Availability</h2>
              <p className="mb-4">
                While we strive to provide continuous service, we do not guarantee uninterrupted access. 
                The Service may be temporarily unavailable due to maintenance, updates, or technical issues.
              </p>
              <p>
                <strong>Service Updates:</strong> We continuously improve the platform. Features may change, and temporary disruptions may occur during scheduled maintenance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Account Termination</h2>
              <p className="mb-4">
                We reserve the right to terminate or suspend accounts that:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Violate these Terms of Service</li>
                <li>Engage in fraudulent or illegal activities</li>
                <li>Remain inactive for extended periods (90+ days without subscription)</li>
                <li>Pose security risks to the platform or other users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Subscription and Payments</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Subscription fees are charged monthly in advance</li>
                <li>All fees are non-refundable except as required by law</li>
                <li>Prices may change with 30 days notice</li>
                <li>Accounts without active subscriptions may be subject to limitations or deletion</li>
                <li>Payment processing is handled by Stripe (subject to their terms)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8a. Hyperliquid Builder-Code Fee</h2>
              <div className="bg-gray-700 p-4 rounded-lg" data-testid="terms-builder-fee-section">
                <p className="mb-3">
                  In addition to standard Hyperliquid exchange fees, actuvo.ai
                  attaches a <strong>builder code</strong> to every order it
                  places on your behalf. This is an on-chain mechanism that
                  Hyperliquid uses to compensate the application routing your
                  trades.
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Fee rate:</strong> 0.01% (1 basis point, equal to
                    one tenth of one cent on every $10 traded) per fill, on
                    both sides of perpetual trades. This is half of
                    Hyperliquid&rsquo;s own maker fee.
                  </li>
                  <li>
                    <strong>How it&rsquo;s charged:</strong> automatically and
                    on-chain by Hyperliquid at fill time. It appears as part
                    of the fee column on your Hyperliquid trade history.
                  </li>
                  <li>
                    <strong>One-time consent:</strong> the first time you
                    start the bot, actuvo.ai submits an <code>ApproveBuilderFee</code>
                    transaction to Hyperliquid using your wallet, approving a
                    maximum of 0.01% for our builder address. By creating an
                    actuvo.ai account and starting the bot, you explicitly
                    consent to this approval being signed on your behalf.
                  </li>
                  <li>
                    <strong>Revocable:</strong> you may revoke the approval
                    at any time directly from the Hyperliquid interface under
                    Account &rarr; Builder Codes. Revocation does not affect
                    your actuvo.ai subscription.
                  </li>
                  <li>
                    <strong>Maximum is capped:</strong> Hyperliquid enforces
                    a 0.1% protocol-level cap on perpetual builder fees. We
                    charge a tenth of that ceiling.
                  </li>
                </ul>
                <p className="mt-3 text-sm text-gray-400">
                  Builder-code revenue helps fund infrastructure, market-data,
                  and development of actuvo.ai. It is a separate revenue stream
                  from your monthly subscription and applies regardless of
                  whether your trades are profitable.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Intellectual Property</h2>
              <p className="mb-4">
                All content, trademarks, and intellectual property related to actuvo.ai remain our exclusive property. 
                Users may not copy, reproduce, or distribute any part of the service without written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Privacy and Data Protection</h2>
              <p className="mb-4">
                Your privacy is important to us. Please review our Privacy Policy to understand how we collect, 
                use, and protect your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Governing Law</h2>
              <p className="mb-4">
                These Terms are governed by the laws of the United Kingdom. 
                Any disputes shall be resolved through binding arbitration or in the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to Terms</h2>
              <p className="mb-4">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. 
                Continued use of the Service constitutes acceptance of modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Information</h2>
              <p className="mb-4">
                For questions about these Terms, please contact us at:
              </p>
              <div className="bg-gray-700 p-4 rounded-lg">
                <p><strong>Email:</strong> legal@actuvo.ai</p>
                <p><strong>Support:</strong> support@actuvo.ai</p>
                <p><strong>Website:</strong> https://actuvo.ai</p>
                <p className="text-sm text-gray-400 mt-2">
                  actuvo.ai is operated as a sole proprietorship. All communications are handled via email.
                </p>
              </div>
            </section>

            <div className="mt-8 p-6 bg-red-900 border border-red-700 rounded-lg">
              <h3 className="text-xl font-semibold text-red-100 mb-3">FINAL WARNING</h3>
              <p className="text-red-100">
                By using actuvo.ai, you acknowledge that you understand the risks involved in automated trading, 
                that you may lose money, and that you are using the service entirely at your own risk. 
                Seek professional financial advice before making investment decisions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;